/*
 * How many calories a body burns in a day, estimated — `/specs/features/expenditure.spec.md`,
 * to the number. The estimate is carried from day to day by a filter over two quantities,
 * the body's true weight and its expenditure, each held with an uncertainty: every day the
 * weight is predicted to move by what was eaten against what was spent, and every weigh-in
 * is read against that prediction, the miss split between the two by how sure the filter
 * is of each. A heavy morning barely registers; a run of them moves the expenditure.
 *
 * Every app works this out for itself from what it already holds — the diary's days, the
 * weigh-ins and the body stats — so the Android port in `data/Expenditure.kt` runs the same
 * cases as `__tests__/expenditure.test.ts`, and the arithmetic here is written in the order
 * the spec gives it so the two ports land on the same calorie.
 */

export type Sex = 'female' | 'male'

export const ACTIVITY_LEVELS = ['sedentary', 'light', 'moderate', 'active'] as const
export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number]

const ACTIVITY_FACTOR: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
}

/** What a kilogram of the scale's movement has proved to be worth, in calories. */
export const KCAL_PER_KG_READ = 6200

/** How much of the way a weigh-in moves the trend weight towards itself. */
const TREND_SHARE = 0.1

/** How far a single scale reading is trusted around the true weight, in kilograms. */
const SCALE_SD = 1.2

/** How far the true weight may wander in a day beyond what was eaten and spent, in kilograms. */
const WANDER_SD = 0.02

/** How far the expenditure may drift in a day, in calories. */
const DRIFT_SD = 10

/** How far the formula's seed is trusted, and how far the first week's mean is. */
const SEED_SD = 400
const LOOSE_SEED_SD = 800

/** The most the named estimate rises in a day, and the most it falls. */
const RISE_CAP = 21
const FALL_CAP = 30

/** How many counted days the seed is the middle of, where there is no formula to start from. */
const SEED_DAYS = 7

/** The share of the expenditure that follows what is eaten before the scale has said anything. */
const FOLLOW_SHARE = 0.2

/** The half-lives, in days, of the three running intakes. */
const IMPUTE_HALF_LIFE = 12
const RECENT_HALF_LIFE = 14
const LONG_HALF_LIFE = 365

/**
 * How far a weigh-in is read from where the filter expected it, in standard deviations of that
 * expectation. A reading past it is read as if it had landed there: a weight typed as 850 for
 * 85.0 is a slip, and taken whole it would drag the expenditure down for months.
 */
const MISS_REACH = 4

/** The fewest calories a day the estimate will say a body burns. */
const LEAST_BURNED = 800

export interface BodyStats {
  sex: Sex | null
  birthDate: string | null
  heightM: number | null
  activityLevel: ActivityLevel
}

export interface DayIntake {
  day: string
  calories: number
}

export interface DayWeight {
  day: string
  weightKg: number
}

export interface ExpenditurePoint {
  day: string
  value: number
}

export interface Expenditure {
  /** Calories a day, rounded, or null where nothing can be said yet. */
  value: number | null
}

/** Every estimate a client draws from, in one answer. */
export interface ExpenditureReading extends Expenditure {
  /** The trend weight at the latest weigh-in, which is the weight the coached way works from. */
  latestWeightKg: number | null
  /** The estimate on every day from the start day to today, for the metrics row. */
  series: ExpenditurePoint[]
}

function ageOn(birthDate: string, day: string): number {
  const born = new Date(`${birthDate}T00:00:00Z`)
  const at = new Date(`${day}T00:00:00Z`)
  let age = at.getUTCFullYear() - born.getUTCFullYear()
  const beforeBirthday =
    at.getUTCMonth() < born.getUTCMonth() ||
    (at.getUTCMonth() === born.getUTCMonth() && at.getUTCDate() < born.getUTCDate())
  if (beforeBirthday) {
    age -= 1
  }
  return age
}

/**
 * What the resting rate adds for a man and takes for a woman — and, where the sex is not
 * said, the midpoint of the two, so an estimate can be made without it.
 */
function sexTerm(sex: Sex | null): number {
  if (sex === 'male') {
    return 5
  }
  if (sex === 'female') {
    return -161
  }
  return -78
}

/**
 * The formula's estimate, or null where a stat it needs is missing. The sex is not one it
 * needs: left unsaid, the estimate sits between a man's and a woman's.
 */
export function seedExpenditure(
  stats: BodyStats,
  weightKg: number | null,
  day: string,
): number | null {
  if (stats.birthDate === null || stats.heightM === null || weightKg === null) {
    return null
  }
  const age = ageOn(stats.birthDate, day)
  const resting = 10 * weightKg + 6.25 * (stats.heightM * 100) - 5 * age + sexTerm(stats.sex)
  return resting * ACTIVITY_FACTOR[stats.activityLevel]
}

/** The trend weight on each weighed day, oldest first. */
export function trendWeights(weights: readonly DayWeight[]): DayWeight[] {
  const sorted = [...weights].sort((a, b) => a.day.localeCompare(b.day))
  const out: DayWeight[] = []
  let trend: number | null = null
  for (const { day, weightKg } of sorted) {
    trend = trend === null ? weightKg : trend + TREND_SHARE * (weightKg - trend)
    out.push({ day, weightKg: trend })
  }
  return out
}

/**
 * The counted days: each logged day's calories, leaving out the days marked incomplete. A day
 * with no entries has no summary to begin with, so it is left out by never being here.
 */
export function countedIntakes(
  days: readonly DayIntake[],
  incompleteDays: readonly string[],
): DayIntake[] {
  const skipped = new Set(incompleteDays)
  return days.filter((each) => !skipped.has(each.day)).sort((a, b) => a.day.localeCompare(b.day))
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000)
}

function dayAfter(day: string, offset: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + offset * 86_400_000).toISOString().slice(0, 10)
}

/** The share a running average moves toward each new reading, for a half-life in days. */
function shareFor(halfLifeDays: number): number {
  return 1 - Math.pow(0.5, 1 / halfLifeDays)
}

/** The middle of seven days' calories: the fourth of them from the least. */
function middleOf(seven: readonly DayIntake[]): number {
  const sorted = seven.map((each) => each.calories).sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)] ?? 0
}

/** Rounded to the calorie and never below the least a body burns. */
function settled(value: number): number {
  return Math.round(Math.max(LEAST_BURNED, value))
}

/**
 * The estimate on every day from the start day to [to]. [intakes] are the counted days,
 * oldest first, and [weighIns] the scale weights in any order. Empty where there is no
 * counted day, or no weigh-in on or after the first — or, without the stats the formula needs,
 * before seven days have been counted and one weighed on or after the seventh.
 */
export function expenditureSeries(
  stats: BodyStats,
  intakes: readonly DayIntake[],
  weighIns: readonly DayWeight[],
  to: string,
): ExpenditurePoint[] {
  const first = intakes[0]
  if (first === undefined) {
    return []
  }
  const hasFormula = stats.birthDate !== null && stats.heightM !== null
  // Without a formula the seed is the middle of the first week, so nothing starts before that
  // week has been logged: an estimate on its second day would be worked out from days to come.
  const opens = hasFormula ? first : intakes[SEED_DAYS - 1]
  if (opens === undefined) {
    return []
  }
  const scale = new Map<string, number>()
  for (const each of weighIns) {
    scale.set(each.day, each.weightKg)
  }
  const start = [...scale.keys()].filter((day) => day >= opens.day).sort()[0]
  if (start === undefined || start > to) {
    return []
  }
  let w = scale.get(start) ?? 0
  const seed = seedExpenditure(stats, w, start) ?? middleOf(intakes.slice(0, SEED_DAYS))
  const calories = new Map(intakes.map((each) => [each.day, each.calories]))
  const imputeShare = shareFor(IMPUTE_HALF_LIFE)
  const recentShare = shareFor(RECENT_HALF_LIFE)
  const longShare = shareFor(LONG_HALF_LIFE)
  /*
   * Each average is the plain mean of the days counted so far until its half-life's share is
   * the larger of the two: a first day logged from the afternoon is one day among the days
   * counted, rather than the figure every average starts from and takes weeks to forget.
   */
  let counted = 0
  let imputed = 0
  let recent = 0
  let long = 0
  const fold = (eaten: number) => {
    counted += 1
    const plain = 1 / counted
    imputed += Math.max(plain, imputeShare) * (eaten - imputed)
    recent += Math.max(plain, recentShare) * (eaten - recent)
    long += Math.max(plain, longShare) * (eaten - long)
  }
  // The diary may have begun before the scale did: those days move the averages first.
  for (let offset = 0; offset <= daysBetween(first.day, start); offset += 1) {
    const eaten = calories.get(dayAfter(first.day, offset))
    if (eaten !== undefined) {
      fold(eaten)
    }
  }
  const K = KCAL_PER_KG_READ
  let E = seed
  let pww = SCALE_SD * SCALE_SD
  let pwe = 0
  let pee = hasFormula ? SEED_SD * SEED_SD : LOOSE_SEED_SD * LOOSE_SEED_SD
  let estimate = E
  const out: ExpenditurePoint[] = [{ day: start, value: settled(estimate) }]
  for (let offset = 1; offset <= daysBetween(start, to); offset += 1) {
    const day = dayAfter(start, offset)
    const eaten = calories.get(day)
    let intake = imputed
    if (eaten !== undefined) {
      intake = eaten
      fold(eaten)
    }
    const following = FOLLOW_SHARE * (recent - long)
    // Predict: the weight moves by the day's energy balance, the expenditure stands.
    w = w + (intake - E - following) / K
    const pwwPredicted = pww - (2 * pwe) / K + pee / (K * K) + WANDER_SD * WANDER_SD
    const pwePredicted = pwe - pee / K
    const peePredicted = pee + DRIFT_SD * DRIFT_SD
    pww = pwwPredicted
    pwe = pwePredicted
    pee = peePredicted
    const weighed = scale.get(day)
    if (weighed !== undefined) {
      const innovation = pww + SCALE_SD * SCALE_SD
      const reach = MISS_REACH * Math.sqrt(innovation)
      const miss = Math.max(-reach, Math.min(reach, weighed - w))
      const weightGain = pww / innovation
      const expenditureGain = pwe / innovation
      w = w + weightGain * miss
      E = E + expenditureGain * miss
      const covarianceBefore = pwe
      pww = pww - weightGain * pww
      pwe = pwe - weightGain * pwe
      pee = pee - expenditureGain * covarianceBefore
    }
    const target = E + following
    estimate = estimate + Math.max(-FALL_CAP, Math.min(RISE_CAP, target - estimate))
    out.push({ day, value: settled(estimate) })
  }
  return out
}

/**
 * The expenditure as a client reads it on [today]: from the logged days and which of them are
 * incomplete, the weigh-ins in any order, and the body stats.
 */
export function expenditureReading(
  stats: BodyStats,
  days: readonly DayIntake[],
  incompleteDays: readonly string[],
  weighIns: readonly DayWeight[],
  today: string,
  /**
   * The day still being logged — the device's today — which is uncounted until it ends. Null
   * where nothing is: a day asked about in the past was over, and counts as it was logged.
   */
  inProgress: string | null = null,
): ExpenditureReading {
  const intakes = countedIntakes(
    days,
    inProgress === null ? incompleteDays : [...incompleteDays, inProgress],
  )
  const trend = trendWeights(weighIns)
  const series = expenditureSeries(stats, intakes, weighIns, today)
  return {
    value: series[series.length - 1]?.value ?? formulaOn(stats, weighIns, today),
    latestWeightKg: trend[trend.length - 1]?.weightKg ?? null,
    series,
  }
}

/**
 * The estimate before the filter has started: the formula, from the trend weight on the latest
 * weigh-in on or before [day] — or null without a date of birth, a height and a weigh-in. A body
 * weighed but not yet fed through the diary still has a budget to be coached to.
 */
function formulaOn(stats: BodyStats, weighIns: readonly DayWeight[], day: string): number | null {
  const trend = trendWeights(weighIns.filter((each) => each.day <= day))
  const seed = seedExpenditure(stats, trend[trend.length - 1]?.weightKg ?? null, day)
  return seed === null ? null : settled(seed)
}

/** The estimate as it stood on [day], from a series, or null where the series has no such day. */
export function estimateOn(series: readonly ExpenditurePoint[], day: string): number | null {
  for (const each of series) {
    if (each.day === day) {
      return each.value
    }
  }
  return null
}

/** What the coached way can be missing: the body stats, kept in settings, or a weight. */
export type GuidedMissing = 'stats' | 'weight'

/**
 * What the coached way lacks before it can work anything out — the stats the formula needs,
 * a weight — or nothing, in which case it has an expenditure to work from.
 */
export function guidedMissing(stats: BodyStats, weightKg: number | null): GuidedMissing[] {
  const missing: GuidedMissing[] = []
  if (stats.birthDate === null || stats.heightM === null) {
    missing.push('stats')
  }
  if (weightKg === null) {
    missing.push('weight')
  }
  return missing
}
